/**
 * ClientShippingControls: the two always-visible shipping boxes on an ACTIVE
 * junction row of `SharedLocationsCard`, in causal order.
 *
 *   1. Shipping provider: this client's override at this location, or Inherit
 *      (the location's default, org fallback included). The Inherit option
 *      names what it resolves to, so the owner never has to cross-reference
 *      the location settings to know how a client ships.
 *   2. Fulfilment write-back: governs MANUAL completion only. Every carrier
 *      sends tracking to Shopify itself (YunExpress through Print Run when the
 *      label is printed), so for a carrier the switch says why it does nothing
 *      instead of silently being off.
 *
 * Whether the switch applies depends on the EFFECTIVE provider. When the
 * client inherits and the location default is still loading or failed to
 * load, that is unknown, so turning it ON is locked until it resolves; a
 * failure says so in amber with its own Retry (DESIGN.md §13.10: a failed
 * read is never indistinguishable from "off"). Turning it OFF is always
 * reachable, since it only ever withdraws a Shopify write.
 *
 * Hand over (a client-only provider): picking it does NOT save. It opens a
 * Shopify tag field pre-filled with "Completed" and a Save button, because the
 * tag is required and is what Print Run writes to the client's live store once
 * every part of an order is handed over. A refusal from the save (no hand over
 * bench at this location, or a store connection that cannot add tags) shows
 * its own line under the box.
 *
 * Translated (`settings` namespace) although the rest of
 * `features/locations/**` is hardcoded English: the 3PL owner reading it,
 * Thread Lab, runs the app in zh. Mutations and the enable-confirm dialog stay
 * with the card, which owns the cache patches.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  SHIPPING_PROVIDER_ORDER,
  SHIPPING_PROVIDER_PRESENTATION,
  type ShippingProvider,
} from '@/features/production-settings/constants/shipping-providers';
import type { HandoverRefusalCode } from '@/lib/trpc-error-utils';

/** Pre-filled tag when an owner first picks Hand over. */
const DEFAULT_HANDOVER_TAG = 'Completed';

/** Mirrors the backend rule: 1 to 40 characters after trimming, no comma. */
function isValidHandoverTag(tag: string): boolean {
  const trimmed = tag.trim();
  return trimmed.length >= 1 && trimmed.length <= 40 && !trimmed.includes(',');
}

/**
 * GA-only provider list for the picker. Never import the backend's
 * `MVP_SELECTABLE_PROVIDERS` here: this registry (shared with ShippingPanel
 * and production settings) is the frontend's single source of truth for what
 * is presentable. A provider marked 'coming_soon' there stays out of the
 * options.
 */
const GA_PROVIDERS = SHIPPING_PROVIDER_ORDER.filter(
  (p) => SHIPPING_PROVIDER_PRESENTATION[p].status === 'ga',
);

const isKnownProvider = (value: string): value is ShippingProvider =>
  Object.prototype.hasOwnProperty.call(SHIPPING_PROVIDER_PRESENTATION, value);

/** The location's own default provider, as read by the card. */
export type LocationDefaultProvider =
  | { status: 'loading' }
  | { status: 'error'; retry: () => void }
  | { status: 'ready'; provider: ShippingProvider };

export interface ClientShippingControlsProps {
  clientOrganizationId: string;
  /** The junction's override. Null, or a value this bundle does not know, means inherit. */
  shippingProvider: string | null | undefined;
  locationDefault: LocationDefaultProvider;
  writebackEnabled: boolean;
  /** The saved hand over tag for this client, when it is on Hand over. */
  handoverTag?: string | null;
  /** Refusal from the last provider save, if it was a hand over refusal. */
  providerRefusal?: HandoverRefusalCode | null;
  providerSaving: boolean;
  writebackSaving: boolean;
  /** `handoverTag` is set (trimmed) only when the provider is 'handover'. */
  onProviderChange: (provider: ShippingProvider | null, handoverTag?: string) => void;
  /** Turning write-back ON writes to the client's live store, so the card confirms first. */
  onRequestWritebackOn: () => void;
  onWritebackOff: () => void;
}

export function ClientShippingControls({
  clientOrganizationId,
  shippingProvider,
  locationDefault,
  writebackEnabled,
  handoverTag,
  providerRefusal,
  providerSaving,
  writebackSaving,
  onProviderChange,
  onRequestWritebackOn,
  onWritebackOff,
}: ClientShippingControlsProps) {
  const { t } = useTranslation('settings');

  const override = shippingProvider != null && isKnownProvider(shippingProvider) ? shippingProvider : null;
  const inherited = locationDefault.status === 'ready' ? locationDefault.provider : null;
  const effective = override ?? inherited;
  const providerLabel = (p: ShippingProvider) =>
    p === 'handover'
      ? t('sharedLocations.shipping.handover')
      : SHIPPING_PROVIDER_PRESENTATION[p]?.label ?? p;

  // Hand over picked but not saved yet: the Select shows it and the tag field
  // opens, nothing is written until Save.
  const [pendingHandover, setPendingHandover] = useState(false);
  const [tagDraft, setTagDraft] = useState<string | null>(null);
  // A saved (or externally changed) provider ends any unsaved pick and draft.
  useEffect(() => {
    setPendingHandover(false);
    setTagDraft(null);
  }, [override, handoverTag]);
  const savedTag = override === 'handover' ? handoverTag ?? null : null;
  const showTagEditor = pendingHandover || override === 'handover';
  const tagValue = tagDraft ?? savedTag ?? DEFAULT_HANDOVER_TAG;
  const tagValid = isValidHandoverTag(tagValue);
  const tagUnchanged = override === 'handover' && !pendingHandover && tagValue.trim() === (savedTag ?? '');

  const handleSelect = (v: string) => {
    if (v === 'handover') {
      if (override !== 'handover') {
        setPendingHandover(true);
        setTagDraft(null);
      }
      return;
    }
    setPendingHandover(false);
    setTagDraft(null);
    onProviderChange(v === 'inherit' ? null : (v as ShippingProvider));
  };

  const handleSaveHandover = () => {
    if (!tagValid) return;
    onProviderChange('handover', tagValue.trim());
  };

  const handleCancelHandover = () => {
    setPendingHandover(false);
    setTagDraft(null);
  };

  // null = unknown (inheriting while the location default is loading or failed).
  const providerUnknown = effective === null;
  const writebackNotApplied = effective !== null && effective !== 'manual';
  const switchLocked = !writebackEnabled && (providerUnknown || writebackNotApplied);

  const providerId = `shipping-provider-${clientOrganizationId}`;
  const providerHelpId = `shipping-provider-help-${clientOrganizationId}`;
  const switchId = `writeback-${clientOrganizationId}`;
  const switchHelpId = `writeback-help-${clientOrganizationId}`;
  const switchReasonId = `writeback-reason-${clientOrganizationId}`;
  const tagId = `handover-tag-${clientOrganizationId}`;
  const tagHelpId = `handover-tag-help-${clientOrganizationId}`;
  const tagErrorId = `handover-tag-error-${clientOrganizationId}`;
  const refusalId = `shipping-provider-refusal-${clientOrganizationId}`;

  const refusalCopy =
    providerRefusal === 'HANDOVER_BENCH_MISSING'
      ? t('sharedLocations.shipping.handoverBenchMissing')
      : providerRefusal === 'HANDOVER_TAG_SCOPE_MISSING'
        ? t('sharedLocations.shipping.handoverTagScopeMissing')
        : null;

  // At most one reason line under the switch: what happens instead for
  // YunExpress, why it does nothing for any other carrier, or why it is
  // locked when the provider cannot be read. Loading says nothing; it is a
  // momentary state and the disabled switch already reads as waiting.
  let reason: JSX.Element | null = null;
  if (effective === 'handover') {
    reason = (
      <p id={switchReasonId} className="mt-1 text-xs text-muted-foreground">
        {t('sharedLocations.writeback.handover')}
      </p>
    );
  } else if (effective === 'yunexpress') {
    reason = (
      <p id={switchReasonId} className="mt-1 text-xs text-muted-foreground">
        {t('sharedLocations.writeback.yunexpress')}
      </p>
    );
  } else if (writebackNotApplied && effective) {
    reason = (
      <p id={switchReasonId} className="mt-1 text-xs text-muted-foreground">
        {t('sharedLocations.writeback.notApplied', { provider: providerLabel(effective) })}
      </p>
    );
  } else if (providerUnknown && locationDefault.status === 'error') {
    // Retry sits beside the sentence, not inside it, so the switch's
    // accessible description is the sentence alone.
    reason = (
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <p id={switchReasonId} className="text-xs text-amber-700">
          {t('sharedLocations.writeback.providerUnknown')}
        </p>
        <Button
          type="button"
          variant="link"
          className="h-auto gap-1 p-0 text-xs text-amber-800 underline underline-offset-2"
          onClick={locationDefault.retry}
        >
          <RefreshCw className="h-3 w-3" aria-hidden="true" />
          {t('production.common.retry')}
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="mt-3 flex items-start justify-between gap-4 rounded-md border bg-muted/30 p-3">
        <div className="min-w-0 flex-1">
          <label htmlFor={providerId} className="text-sm font-medium leading-snug">
            {t('sharedLocations.shipping.providerLabel')}
          </label>
          <p id={providerHelpId} className="mt-1.5 text-xs text-muted-foreground">
            {t('sharedLocations.shipping.providerHelp')}
          </p>
        </div>
        <Select
          value={
            pendingHandover
              ? 'handover'
              : override && (GA_PROVIDERS as readonly string[]).includes(override)
                ? override
                : 'inherit'
          }
          onValueChange={handleSelect}
          disabled={providerSaving}
        >
          <SelectTrigger
            id={providerId}
            aria-describedby={providerHelpId}
            className="h-9 w-52 shrink-0 text-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="inherit">
              {inherited
                ? t('sharedLocations.shipping.inheritWith', { provider: providerLabel(inherited) })
                : t('sharedLocations.shipping.inherit')}
            </SelectItem>
            {GA_PROVIDERS.map((p) => (
              <SelectItem key={p} value={p}>
                {providerLabel(p)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {showTagEditor && (
        <div className="mt-2 rounded-md border bg-muted/30 p-3">
          <label htmlFor={tagId} className="text-sm font-medium leading-snug">
            {t('sharedLocations.shipping.handoverTagLabel')}
          </label>
          <p id={tagHelpId} className="mt-1.5 text-xs text-muted-foreground">
            {t('sharedLocations.shipping.handoverTagHelp')}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Input
              id={tagId}
              value={tagValue}
              onChange={(e) => setTagDraft(e.target.value)}
              maxLength={60}
              aria-invalid={!tagValid}
              aria-describedby={tagValid ? tagHelpId : `${tagHelpId} ${tagErrorId}`}
              className="h-9 w-52 text-xs"
              disabled={providerSaving}
            />
            <Button
              type="button"
              size="sm"
              className="h-9"
              onClick={handleSaveHandover}
              disabled={providerSaving || !tagValid || tagUnchanged}
            >
              {t('sharedLocations.shipping.handoverSave')}
            </Button>
            {pendingHandover && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-9"
                onClick={handleCancelHandover}
                disabled={providerSaving}
              >
                {t('sharedLocations.shipping.handoverCancel')}
              </Button>
            )}
          </div>
          {!tagValid && (
            <p id={tagErrorId} className="mt-1 text-xs text-destructive">
              {t('sharedLocations.shipping.handoverTagInvalid')}
            </p>
          )}
        </div>
      )}

      {refusalCopy && (
        <p id={refusalId} role="alert" className="mt-2 text-xs text-amber-700">
          {refusalCopy}
        </p>
      )}

      <div className="mt-3 flex items-start justify-between gap-4 rounded-md border bg-muted/30 p-3">
        <div className="min-w-0 flex-1">
          <label htmlFor={switchId} className="text-sm font-medium leading-snug">
            {t('sharedLocations.writeback.label')}
          </label>
          <p id={switchHelpId} className="mt-1.5 text-xs text-muted-foreground">
            {t('sharedLocations.writeback.help')}
          </p>
          {reason}
        </div>
        <Switch
          id={switchId}
          aria-describedby={reason ? `${switchHelpId} ${switchReasonId}` : switchHelpId}
          checked={writebackEnabled}
          // Off stays reachable: a switch left on from before the provider
          // changed, or while the provider cannot be read, can always be
          // turned off.
          disabled={writebackSaving || switchLocked}
          onCheckedChange={(checked) => (checked ? onRequestWritebackOn() : onWritebackOff())}
        />
      </div>
    </>
  );
}
